import Service from '@ember/service';
import { service } from '@ember/service';
import { alias } from '@ember/object/computed';
import Consts from '../utils/constants';

export default class DriverService extends Service {
    @service('data') model;
    @service selectionData;

    // First RF validation = extra special (specifically, ignore parts of it?)
    _firstRfValidation = true;

    @alias('model.rfDisabled') rfDisabled;
    @alias('model.rfRemoved') rfRemoved;
    @alias('model.cfDisabled') cfDisabled;
    // @alias('model.cfRemoved') cfRemoved;

    resetGain(updatable = true) {
        let fixedGain = this.selectionData.selectedDriver.fixedGain;
        if (fixedGain) {
            if (updatable) this.set('model.gain', fixedGain);
            this.set('gainDisabled', true);
        }
        // If follower, set gain to 1 and disable.
        else if (this.selectionData.selectedTopology.id === Consts.TOPOLOGY.FOLLOWER) {
            if (updatable) this.set('model.gain', 1);
            this.set('gainDisabled', true);
        } else {
            this.set('gainDisabled', false);
        }

        this.set('_firstRfValidation', false);
    }

    resetRf(updatable = true) {
        let selectedAdcType = this.selectionData.selectedAdc.type; // 1st column
        let selectedSourceType = this.selectionData.selectedSourceType.id; // 2nd column - "source" == "input" in documentation vs tool UI
        let essentiallyFollower = this.selectionData.essentiallyFollower;

        // If follower topology
        // and if "from selected adc data" = "Single-Ended"
        // also Grey out Rf field, and set Rf to 0
        // (also selectedSourceType needs to be the same as selectedAdc.type)
        let fixedGain = this.selectionData.selectedDriver.fixedGain;

        if (fixedGain) {
            if (updatable) this.set('model.rf', this.selectionData.selectedDriver.rfDefault);
            this.set('rfDisabled', true);
            this.set('rfRemoved', false);
        } else if (
            essentiallyFollower &&
            ((selectedAdcType == Consts.ADC_TYPE.SE && selectedSourceType == Consts.SOURCE_TYPE.SE) ||
                (selectedAdcType == Consts.ADC_TYPE.DIFF && selectedSourceType == Consts.SOURCE_TYPE.DIFF))
        ) {
            if (updatable) this.set('model.rf', 0);
            this.set('rfDisabled', true);

            // remove rf from noise contributor and circuit-panel
            this.set('rfRemoved', true);
        } else {
            this.enableRf(updatable);
        }
        this.set('_firstRfValidation', false);
    }

    defaultRf(updatable = true) {
        let rf = this.selectionData.selectedDriver.rfDefault;
        if (updatable) this.set('model.rf', rf);
    }

    enableRf(updatable = true) {
        this.set('rfDisabled', false);
        this.set('rfRemoved', false);
        // Don't change rf value based on URL unless it is a non-first (subsequent?) load.
        if (this._firstRfValidation) return;
        if (updatable) this.set('model.rf', this.selectionData.selectedDriver.rfDefault);
    }

    resetCf(updatable = true) {
        const selectedAdcType = this.selectionData.selectedAdc.type; // 1st column
        const selectedSourceType = this.selectionData.selectedSourceType.id; // 2nd column - "source" == "input" in documentation vs tool UI
        const essentiallyFollower = this.selectionData.essentiallyFollower;
        const topology = this.selectionData.selectedTopology.id;
        const driverType = this.selectionData.selectedDriver.type;

        // Only want Cf settable if adcinput matches source for amp in inverting and non-inverting >1 or inverting || !essentiallyFollower
        // or adcinput matches drivertype and inverting
        // If follower topology
        // and if "from selected adc data" = "Single-Ended"
        // also Grey out Cf field, and set Cf to 0
        // (also selectedSourceType needs to be the same as selectedAdc.type)
        const fixedGain = this.selectionData.selectedDriver.fixedGain;

        if (fixedGain) {
            if (updatable) this.set('model.cf', 0);
            this.set('cfDisabled', true);
        } else if (
            !essentiallyFollower &&
            driverType === Consts.DRIVER_TYPE.AMP &&
            ((selectedAdcType === Consts.ADC_TYPE.SE && selectedSourceType === Consts.SOURCE_TYPE.SE) ||
                (selectedAdcType === Consts.ADC_TYPE.DIFF && selectedSourceType === Consts.SOURCE_TYPE.DIFF))
        ) {
            if (updatable) this.set('model.cf', this.selectionData.selectedDriver.cfDefault || 0);
            this.set('cfDisabled', false);
        } else if (
            selectedAdcType === Consts.ADC_TYPE.DIFF &&
            driverType === Consts.DRIVER_TYPE.DIFF_AMP &&
            topology === Consts.TOPOLOGY.INVERTING
        ) {
            if (updatable) this.set('model.cf', this.selectionData.selectedDriver.cfDefault || 0);
            this.set('cfDisabled', false);
        } else {
            if (updatable) this.set('model.cf', 0);
            this.set('cfDisabled', true);
        }
        this.set('_firstCfValidation', false);
    }

    get _cfDefault() {
        if (this.selectionData.selectedAdc?.has_defaultDriverCf) {
            return this.selectionData.selectedDriver.cfDefault || 0;
        }
        return 0;
    }

    defaultCf(updatable = true) {
        if (updatable) this.set('model.cf', this._cfDefault);
    }

    enableCf(updatable = true) {
        this.set('cfDisabled', false);
        // this.set('cfRemoved', false);
        // Don't change cf value based on URL unless it is a non-first (subsequent?) load.
        if (this._firstCfValidation) return;
        if (updatable) this.set('model.cf', this._cfDefault);
    }

    setTopologyStatus(updatable = true) {
        let enabled = this.selectionData.selectedDriver.type !== Consts.DRIVER_TYPE.DIFF_AMP,
            options = this.selectionData.topologyOptions,
            nonInvertingId = Consts.TOPOLOGY.NON_INVERTING,
            followerId = Consts.TOPOLOGY.FOLLOWER;

        // The options should be modified by reference
        options[followerId - 1].isDisabled = !enabled;
        options[nonInvertingId - 1].isDisabled = !enabled;

        if (
            !enabled &&
            updatable &&
            (this.selectionData.selectedTopology.id === nonInvertingId ||
                this.selectionData.selectedTopology.id === followerId)
        ) {
            this.set(
                'selectionData.selectedTopology',
                this.selectionData.findOneOptionById('topology', Consts.TOPOLOGY.INVERTING),
            );
        }
    }
}
